/**
 * The single fetch helper.
 *
 * Every call in the app goes through here so that the `{ ok, error }` envelope
 * defined in controlpanel/API.md is unwrapped in exactly one place, and so that
 * a 401 from any endpoint has exactly one consequence: the session is dropped
 * and the auth gate bounces the user to /login.
 */

const BASE = '/api';

/** Registered by AuthProvider. Called for any 401 that isn't opted out. */
let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function isAbort(err) {
  return err && (err.name === 'AbortError' || err.code === 20);
}

/**
 * @param {string} path        Path below /api, e.g. '/sites/pnf'
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {object} [options.body]            JSON-serialised automatically
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.skipAuthRedirect] Set on /login and the initial /me
 *        probe, where a 401 is an expected answer rather than a lost session.
 * @returns {Promise<object>} the parsed success envelope (has `ok`, plus
 *          `result` or `user` depending on the endpoint).
 */
export async function api(path, options = {}) {
  const { method = 'GET', body, signal, skipAuthRedirect = false } = options;

  const init = {
    method,
    signal,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  };

  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(BASE + path, init);
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new ApiError('Cannot reach the control panel API.', 0);
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.status === 401) {
    const error = new ApiError(payload?.error || 'Not signed in', 401);
    if (!skipAuthRedirect && unauthorizedHandler) unauthorizedHandler(error);
    throw error;
  }

  if (!response.ok || !payload || payload.ok !== true) {
    const message =
      payload?.error ||
      (response.ok
        ? 'The server returned a response this UI could not read.'
        : `Request failed (HTTP ${response.status}).`);
    throw new ApiError(message, response.status);
  }

  return payload;
}

/** Convenience for the read endpoints, all of which answer with `result`. */
export async function apiResult(path, options) {
  const payload = await api(path, options);
  return payload.result;
}

export const endpoints = {
  me: (signal) => api('/me', { signal, skipAuthRedirect: true }),
  login: (username, password) =>
    api('/login', {
      method: 'POST',
      body: { username, password },
      skipAuthRedirect: true,
    }),
  logout: () => api('/logout', { method: 'POST' }),
  passwordPolicy: (signal) => apiResult('/password-policy', { signal }),
  updatePasswordPolicy: (policy) => apiResult('/password-policy', { method: 'PUT', body: policy }),
  changePassword: (currentPassword, newPassword) =>
    api('/me/password', { method: 'POST', body: { currentPassword, newPassword } }),
  ownSessions: (signal) => apiResult('/me/sessions', { signal }),
  ownActivity: (signal) => apiResult('/me/activity', { signal }),
  revokeOtherSessions: () => apiResult('/me/sessions/revoke', { method: 'POST' }),
  revokeOwnSession: (sessionId) =>
    apiResult(`/me/sessions/${encodeURIComponent(sessionId)}/revoke`, { method: 'POST' }),

  users: (signal) => apiResult('/users', { signal }),
  createUser: (user) => apiResult('/users', { method: 'POST', body: user }),
  updateUser: (username, changes) =>
    apiResult(`/users/${encodeURIComponent(username)}`, { method: 'PATCH', body: changes }),
  resetUserPassword: (username, password, mustChangePassword) =>
    apiResult(`/users/${encodeURIComponent(username)}/password`, {
      method: 'POST',
      body: { password, mustChangePassword },
    }),
  revokeUserSessions: (username) =>
    apiResult(`/users/${encodeURIComponent(username)}/sessions/revoke`, { method: 'POST' }),
  userActivity: (username, signal) =>
    apiResult(`/users/${encodeURIComponent(username)}/activity`, { signal }),

  health: (signal) => apiResult('/health', { signal }),
  pins: (signal) => apiResult('/pins', { signal }),
  pinSite: (name) => apiResult(`/pins/${encodeURIComponent(name)}`, { method: 'PUT' }),
  unpinSite: (name) => apiResult(`/pins/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  sites: (signal) => apiResult('/sites', { signal }),
  site: (name, signal) => apiResult(`/sites/${encodeURIComponent(name)}`, { signal }),
  platform: (signal) => apiResult('/platform', { signal }),
  siteLogs: (name, slot, lines, signal) =>
    apiResult(`/sites/${encodeURIComponent(name)}/logs?slot=${encodeURIComponent(slot)}&lines=${encodeURIComponent(lines)}`, {
      signal,
    }),
  promoteSite: (name, version) =>
    apiResult(`/sites/${encodeURIComponent(name)}/promote`, {
      method: 'POST',
      body: version ? { version } : {},
    }),
  addDomain: (name, domain, slot) =>
    apiResult(`/sites/${encodeURIComponent(name)}/domains`, {
      method: 'POST',
      body: { domain, slot },
    }),
  removeDomain: (name, domain) =>
    apiResult(`/sites/${encodeURIComponent(name)}/domains/${encodeURIComponent(domain)}`, {
      method: 'DELETE',
    }),
  issueCertificate: (name, domain) =>
    apiResult(`/sites/${encodeURIComponent(name)}/domains/${encodeURIComponent(domain)}/certificate`, {
      method: 'POST',
    }),
  config: (signal) => apiResult('/config', { signal }),
  audit: (limit, signal) => apiResult(`/audit?limit=${encodeURIComponent(limit)}`, { signal }),
};
